import { Component, OnInit, Input, forwardRef } from '@angular/core';
import {
    FormControl,
    Validators,
    NG_VALUE_ACCESSOR,
    ControlValueAccessor
} from '@angular/forms';
import { catchError, finalize, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatCheckbox } from "@angular/material/checkbox";
import { AdminisSharedModule } from "../adminis-shared.module";
import { NgIf, NgStyle } from "@angular/common";
import { DocumentLibraryService } from "../../../services/http/document-library.service";

@Component({
    selector: "app-compulsory-document-download",
    templateUrl: "./compulsory-document-download.component.html",
    styleUrls: ["./compulsory-document-download.component.scss"],
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CompulsoryDocumentDownloadComponent),
            multi: true,
        },
    ],
    imports: [MatCheckbox, AdminisSharedModule, NgIf, NgStyle],
})
export class CompulsoryDocumentDownloadComponent implements OnInit, ControlValueAccessor {
    @Input() id: string;
    
    @Input() public fontSize: string = "inherit";
    
    @Input() public documentToken!: string;
    @Input() public documentName: string = "Product Disclosure";
    @Input() public downloadText: string = "download";
    @Input() public downloadingText: string = "Downloading...";
    @Input() public checkboxText: string = "You must download and read the";

    protected documentControl = new FormControl(false, [Validators.requiredTrue]);
    protected isDownloading = false;
    protected hasDownloadError = false;
    protected downloadComplete = false;

    private onChange: any = () => {};
    private onTouched: any = () => {};

    constructor(private documentService: DocumentLibraryService) {}

    ngOnInit(): void {
        this.documentControl.valueChanges.subscribe((value) => {
            this.onChange(value);
        });
    }

    // ControlValueAccessor implementation
    writeValue(value: any): void {
        if (value !== undefined) {
            this.documentControl.setValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.documentControl.disable() : this.documentControl.enable();
    }

    // Public API for parent component (via ViewChild)
    public markAsTouched(opts?: { onlySelf?: boolean }): void {
        this.documentControl.markAsTouched(opts);
        this.onTouched();
    }

    public markAsDirty(opts?: { onlySelf?: boolean }): void {
        this.documentControl.markAsDirty(opts);
    }

    public markAsPristine(opts?: { onlySelf?: boolean }): void {
        this.documentControl.markAsPristine(opts);
    }

    public markAsUntouched(opts?: { onlySelf?: boolean }): void {
        this.documentControl.markAsUntouched(opts);
    }

    public isTouched(): boolean {
        return this.documentControl.touched;
    }

    public isDirty(): boolean {
        return this.documentControl.dirty;
    }

    // Handles download click
    protected onDocumentLinkClick(event: MouseEvent): void {
        event.preventDefault();

        if (this.isDownloading) return;

        this.downloadDocumentFile();
    }

    private downloadDocumentFile(): void {
        this.isDownloading = true;
        this.hasDownloadError = false;

        this.documentService
            .downloadDocumentFromToken(this.documentToken)
            .pipe(
                tap((response) => {
                    const url = window.URL.createObjectURL(response.blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = response.fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.markAsDownloaded();
                }),
                catchError(() => {
                    this.hasDownloadError = true;
                    return of(null);
                }),
                finalize(() => {
                    this.isDownloading = false;
                }),
            )
            .subscribe();
    }

    private markAsDownloaded(): void {
        this.downloadComplete = true;
    }

    protected onCheckboxChange(event: any): void {
        if (event.checked && !this.downloadComplete) {
            this.documentControl.setValue(false, { emitEvent: true });
        }
    }

    get textStyles() {
        return {
            "font-size": this.fontSize,
        };
    }
}